import { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Input } from '@/app/components/ui/input';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { Badge } from '@/app/components/ui/badge';
import { toast } from '@/app/components/ui/simple-toaster';
import { Alert, AlertTitle, AlertDescription } from '@/app/components/ui/alert';
import { useSubscription } from '@/hooks/use-billing';
import { useOrgToday } from '@/hooks/use-settings';
import { handleApiError } from '@/lib/api-client';
import {
  useImportAssets,
  useDownloadTemplate,
  autoDetectColumnMapping,
  parseCSVPreview,
  groupImportErrors,
  generateErrorReport,
  H3GEAR_ASSET_FIELDS,
  type ColumnMapping,
  type ImportOptions,
  type ImportResponse,
  type GroupedErrors,
} from '@/hooks/use-import';

type WizardStep = 'upload' | 'mapping' | 'preview' | 'import';

const STEPS: { id: WizardStep; label: string; number: number }[] = [
  { id: 'upload', label: 'Upload File', number: 1 },
  { id: 'mapping', label: 'Map Columns', number: 2 },
  { id: 'preview', label: 'Preview & Validate', number: 3 },
  { id: 'import', label: 'Import', number: 4 },
];

export function ImportWizardPage() {
  const navigate = useNavigate();
  const importMutation = useImportAssets();
  const downloadTemplateMutation = useDownloadTemplate();
  const { data: subscriptionData } = useSubscription();
  const orgToday = useOrgToday();

  const usage = subscriptionData?.subscription?.usage;
  const assetLimit = usage?.max_assets ?? null;
  const assetCount = usage?.asset_count ?? 0;
  const pctAssets = usage?.pct_assets ?? 0;
  const atCap = assetLimit !== null && assetCount >= assetLimit;
  const nearCap = assetLimit !== null && pctAssets >= 80;
  const creationBlocked = Boolean(
    subscriptionData?.subscription?.asset_limit?.is_creation_blocked
  );

  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvPreviewRows, setCsvPreviewRows] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping[]>([]);
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    dryRun: false,
    updateMode: 'upsert',
    createInitialInspection: false,
    inspectionDate: orgToday,
  });
  const [validationResult, setValidationResult] = useState<ImportResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [expandedErrorGroups, setExpandedErrorGroups] = useState<Set<string>>(new Set());

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    
    try {
      const text = await selectedFile.text();
      const { headers, rows } = parseCSVPreview(text, 5);
      
      if (headers.length === 0) {
        toast.error('Invalid CSV file', {
          description: 'The file appears to be empty or invalid.',
        });
        return;
      }

      setCsvHeaders(headers);
      setCsvPreviewRows(rows);
      
      const autoMapping = autoDetectColumnMapping(headers);
      setColumnMapping(autoMapping);
      
      setCurrentStep('mapping');
    } catch (_error) {
      toast.error('Failed to read file', {
        description: 'Please ensure the file is a valid CSV.',
      });
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && droppedFile.type === 'text/csv') {
        handleFileSelect(droppedFile);
      } else if (droppedFile && droppedFile.name.endsWith('.csv')) {
        handleFileSelect(droppedFile);
      } else {
        toast.error('Invalid file type', {
          description: 'Please upload a CSV file.',
        });
      }
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect]
  );

  const updateColumnMapping = useCallback((csvColumn: string, h3gearField: string | null) => {
    setColumnMapping((prev) =>
      prev.map((m) =>
        m.csvColumn === csvColumn ? { ...m, h3gearField } : m
      )
    );
  }, []);

  const hasRequiredFields = useMemo(() => {
    const mappedFields = columnMapping
      .filter((m) => m.h3gearField)
      .map((m) => m.h3gearField);
    return mappedFields.includes('serial_number');
  }, [columnMapping]);

  const handleValidate = useCallback(async () => {
    if (!file) return;

    setIsValidating(true);
    try {
      const result = await importMutation.mutateAsync({
        file,
        columnMapping,
        options: { ...importOptions, dryRun: true },
      });
      setValidationResult(result);
      setCurrentStep('preview');
    } catch (error) {
      const apiError = handleApiError(error);
      if (apiError.code === 'plan_limit_exceeded') {
        toast.error('Validation failed', {
          description: apiError.message,
          action: {
            label: 'Upgrade plan',
            onClick: () => {
              window.location.href = '/settings/subscription';
            },
          },
        });
      } else {
        toast.error('Validation failed', {
          description: apiError.message,
        });
      }
    } finally {
      setIsValidating(false);
    }
  }, [file, columnMapping, importOptions, importMutation]);

  const handleImport = useCallback(async () => {
    if (!file) return;

    try {
      const result = await importMutation.mutateAsync({
        file,
        columnMapping,
        options: { ...importOptions, dryRun: false },
      });
      setImportResult(result);
      setCurrentStep('import');
      
      if (result.summary.errors === 0) {
        toast.success('Import completed', {
          description: `Successfully imported ${result.summary.created} new assets and updated ${result.summary.updated} existing assets.`,
        });
      } else {
        toast.warning('Import completed with errors', {
          description: `${result.summary.created + result.summary.updated} assets processed, ${result.summary.errors} errors.`,
        });
      }
    } catch (error) {
      const apiError = handleApiError(error);
      if (apiError.code === 'plan_limit_exceeded') {
        toast.error('Import failed', {
          description: apiError.message,
          action: {
            label: 'Upgrade plan',
            onClick: () => {
              window.location.href = '/settings/subscription';
            },
          },
        });
      } else {
        toast.error('Import failed', {
          description: apiError.message,
        });
      }
    }
  }, [file, columnMapping, importOptions, importMutation]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadTemplateMutation.mutateAsync();
      toast.success('Template downloaded');
    } catch (_error) {
      toast.error('Failed to download template');
    }
  }, [downloadTemplateMutation]);

  const handleDownloadErrorReport = useCallback(() => {
    const results = importResult?.results || validationResult?.results || [];
    const report = generateErrorReport(results);
    if (!report) return;

    const blob = new Blob([report], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import_errors.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [importResult, validationResult]);

  const toggleErrorGroup = useCallback((message: string) => {
    setExpandedErrorGroups((prev) => {
      const next = new Set(prev);
      if (next.has(message)) {
        next.delete(message);
      } else {
        next.add(message);
      }
      return next;
    });
  }, []);

  const groupedErrors = useMemo((): GroupedErrors[] => {
    const results = validationResult?.results || importResult?.results || [];
    return groupImportErrors(results);
  }, [validationResult, importResult]);

  const goBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].id);
    }
  }, [currentStepIndex]);

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              index < currentStepIndex
                ? 'bg-green-600 text-white'
                : index === currentStepIndex
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {index < currentStepIndex ? (
              <Check className="h-4 w-4" />
            ) : (
              step.number
            )}
          </div>
          <span
            className={`ml-2 text-sm ${
              index === currentStepIndex ? 'font-medium text-gray-900' : 'text-gray-500'
            }`}
          >
            {step.label}
          </span>
          {index < STEPS.length - 1 && (
            <div
              className={`w-12 h-0.5 mx-4 ${
                index < currentStepIndex ? 'bg-green-600' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderUploadStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Upload CSV File</CardTitle>
        <CardDescription>
          Upload a CSV file containing your asset data. The file should have headers in the first row.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-1">
            Drop your CSV file here
          </p>
          <p className="text-sm text-gray-500 mb-4">or click to browse</p>
          <input
            id="file-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileInputChange}
          />
          <Button variant="outline" type="button">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Select File
          </Button>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Need a template?</p>
            <p className="text-sm text-gray-500">
              Download our sample CSV template with all supported columns
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={downloadTemplateMutation.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderMappingStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Map Columns</CardTitle>
        <CardDescription>
          Match your CSV columns to H3ight fields. Serial Number is required.
          {columnMapping.filter((m) => m.h3gearField).length > 0 && (
            <span className="ml-2 text-green-600">
              ({columnMapping.filter((m) => m.h3gearField).length} columns auto-detected)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">CSV Column</TableHead>
                <TableHead className="w-1/3">Sample Data</TableHead>
                <TableHead className="w-1/3">H3ight Field</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {csvHeaders.map((header, index) => {
                const mapping = columnMapping.find((m) => m.csvColumn === header);
                const sampleData = csvPreviewRows[0]?.[index] || '';
                const isRequired = mapping?.h3gearField === 'serial_number';
                
                return (
                  <TableRow key={header}>
                    <TableCell className="font-medium">
                      {header}
                      {isRequired && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          Required
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500 truncate max-w-[200px]">
                      {sampleData || <span className="italic">Empty</span>}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={mapping?.h3gearField || 'skip'}
                        onValueChange={(value) =>
                          updateColumnMapping(header, value === 'skip' ? null : value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Skip this column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="skip">
                            <span className="text-gray-500">Skip this column</span>
                          </SelectItem>
                          {H3GEAR_ASSET_FIELDS.map((field) => {
                            const isAlreadyMapped = columnMapping.some(
                              (m) => m.h3gearField === field.value && m.csvColumn !== header
                            );
                            return (
                              <SelectItem
                                key={field.value}
                                value={field.value}
                                disabled={isAlreadyMapped}
                              >
                                {field.label}
                                {field.required && ' *'}
                                {isAlreadyMapped && ' (already mapped)'}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {!hasRequiredFields && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Missing required field</p>
              <p className="text-sm text-red-700">
                You must map a column to Serial Number to continue.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-900">Import Options</h3>
          
          <div className="space-y-2">
            <Label>Update Mode</Label>
            <Select
              value={importOptions.updateMode}
              onValueChange={(value) =>
                setImportOptions((prev) => ({
                  ...prev,
                  updateMode: value as ImportOptions['updateMode'],
                }))
              }
            >
              <SelectTrigger className="w-full sm:w-[300px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upsert">
                  Create new & update existing
                </SelectItem>
                <SelectItem value="insert_only">
                  Create new only (skip existing)
                </SelectItem>
                <SelectItem value="update_only">
                  Update existing only (skip new)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="create-inspection"
              checked={importOptions.createInitialInspection}
              onCheckedChange={(checked) =>
                setImportOptions((prev) => ({
                  ...prev,
                  createInitialInspection: checked === true,
                }))
              }
            />
            <div className="space-y-1">
              <Label htmlFor="create-inspection" className="cursor-pointer">
                Create initial inspection for imported assets
              </Label>
              <p className="text-sm text-gray-500">
                Automatically create a passing initial inspection record
              </p>
            </div>
          </div>

          {importOptions.createInitialInspection && (
            <div className="ml-6 space-y-2">
              <Label htmlFor="inspection-date">Inspection Date</Label>
              <Input
                id="inspection-date"
                type="date"
                value={importOptions.inspectionDate}
                onChange={(e) =>
                  setImportOptions((prev) => ({
                    ...prev,
                    inspectionDate: e.target.value,
                  }))
                }
                className="w-full sm:w-[200px]"
              />
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleValidate}
            disabled={!hasRequiredFields || isValidating}
          >
            {isValidating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              <>
                Validate & Preview
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderPreviewStep = () => {
    const summary = validationResult?.summary;
    const hasErrors = (summary?.errors || 0) > 0;

    const wouldCreate = (summary?.created || 0) > 0;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Preview & Validate</CardTitle>
          <CardDescription>
            Review the validation results before importing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {creationBlocked && wouldCreate && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Counted asset creation blocked</AlertTitle>
              <AlertDescription>
                Counted new assets will be reported as row errors. Existing-asset
                updates and new non-counted assets can still be imported.{' '}
                <Link to="/settings/subscription" className="underline font-medium">
                  Upgrade your plan
                </Link>{' '}
                or stay at or under the cap for seven consecutive days.
              </AlertDescription>
            </Alert>
          )}
          {!creationBlocked && nearCap && wouldCreate && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                {atCap ? 'Asset cap grace period' : 'Approaching asset limit'}
              </AlertTitle>
              <AlertDescription>
                {atCap
                  ? 'You can keep importing assets during the grace period. '
                  : `You are using ${pctAssets.toFixed(1)}% of your ${assetLimit} asset limit. `}
                <Link to="/settings/subscription" className="underline font-medium">
                  {atCap ? 'View your deadline and upgrade options' : 'Upgrade your plan'}
                </Link>
                {!atCap && ' to avoid running out.'}
              </AlertDescription>
            </Alert>
          )}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{summary.created}</p>
                <p className="text-sm text-green-600">To Create</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{summary.updated}</p>
                <p className="text-sm text-blue-600">To Update</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-700">{summary.skipped}</p>
                <p className="text-sm text-gray-600">To Skip</p>
              </div>
              <div className={`p-4 rounded-lg text-center ${hasErrors ? 'bg-red-50' : 'bg-gray-50'}`}>
                <p className={`text-2xl font-bold ${hasErrors ? 'text-red-700' : 'text-gray-700'}`}>
                  {summary.errors}
                </p>
                <p className={`text-sm ${hasErrors ? 'text-red-600' : 'text-gray-600'}`}>Errors</p>
              </div>
            </div>
          )}

          {hasErrors && groupedErrors.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Errors by Type</h3>
                <Button variant="outline" size="sm" onClick={handleDownloadErrorReport}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Error Report
                </Button>
              </div>
              
              <div className="space-y-2">
                {groupedErrors.map((group) => (
                  <div key={group.message} className="border rounded-lg">
                    <button
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                      onClick={() => toggleErrorGroup(group.message)}
                    >
                      <div className="flex items-center gap-2">
                        {expandedErrorGroups.has(group.message) ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="text-sm text-red-700">{group.message}</span>
                      </div>
                      <Badge variant="secondary">{group.count} rows</Badge>
                    </button>
                    {expandedErrorGroups.has(group.message) && (
                      <div className="px-3 pb-3 pt-1 border-t bg-gray-50">
                        <p className="text-xs text-gray-500">
                          Affected rows: {group.rows.slice(0, 20).join(', ')}
                          {group.rows.length > 20 && ` and ${group.rows.length - 20} more`}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!hasErrors && summary && (summary.created > 0 || summary.updated > 0) && (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Validation passed</p>
                <p className="text-sm text-green-700">
                  All rows are valid and ready to import.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={goBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Mapping
            </Button>
            <Button
              onClick={handleImport}
              disabled={
                importMutation.isPending ||
                (summary?.created === 0 && summary?.updated === 0)
              }
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  Import {(summary?.created || 0) + (summary?.updated || 0)} Assets
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderImportStep = () => {
    const summary = importResult?.summary;
    const hasErrors = (summary?.errors || 0) > 0;
    const totalProcessed = (summary?.created || 0) + (summary?.updated || 0);

    return (
      <Card>
        <CardHeader>
          <CardTitle>Import Complete</CardTitle>
          <CardDescription>
            Your assets have been imported successfully.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {summary && (
            <>
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  {hasErrors ? (
                    <AlertCircle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                  ) : (
                    <CheckCircle2 className="h-16 w-16 mx-auto text-green-500 mb-4" />
                  )}
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {hasErrors ? 'Import Completed with Errors' : 'Import Successful'}
                  </h3>
                  <p className="text-gray-600">
                    {totalProcessed} assets processed
                    {hasErrors && `, ${summary.errors} errors`}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{summary.created}</p>
                  <p className="text-sm text-green-600">Created</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{summary.updated}</p>
                  <p className="text-sm text-blue-600">Updated</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-700">{summary.skipped}</p>
                  <p className="text-sm text-gray-600">Skipped</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${hasErrors ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <p className={`text-2xl font-bold ${hasErrors ? 'text-red-700' : 'text-gray-700'}`}>
                    {summary.errors}
                  </p>
                  <p className={`text-sm ${hasErrors ? 'text-red-600' : 'text-gray-600'}`}>Errors</p>
                </div>
              </div>

              {hasErrors && groupedErrors.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Errors</h3>
                    <Button variant="outline" size="sm" onClick={handleDownloadErrorReport}>
                      <Download className="h-4 w-4 mr-2" />
                      Download Error Report
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {groupedErrors.slice(0, 5).map((group) => (
                      <div key={group.message} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <span className="text-sm text-red-700">{group.message}</span>
                        <Badge variant="secondary">{group.count} rows</Badge>
                      </div>
                    ))}
                    {groupedErrors.length > 5 && (
                      <p className="text-sm text-gray-500 text-center">
                        And {groupedErrors.length - 5} more error types...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => navigate('/assets')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Assets
            </Button>
            <Button onClick={() => {
              setCurrentStep('upload');
              setFile(null);
              setCsvHeaders([]);
              setCsvPreviewRows([]);
              setColumnMapping([]);
              setValidationResult(null);
              setImportResult(null);
            }}>
              Import More Assets
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/assets')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Import Assets</h1>
          <p className="text-gray-600 mt-1">
            Bulk import assets from a CSV file
          </p>
        </div>
      </div>

      {(nearCap || atCap) && (
        <Alert variant={creationBlocked ? 'destructive' : 'default'}>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {creationBlocked
              ? 'Asset creation blocked'
              : atCap
                ? 'Asset cap grace period'
                : 'Approaching asset limit'}
          </AlertTitle>
          <AlertDescription>
            {creationBlocked
              ? <>Your asset-cap grace period has ended. Bulk imports that create new assets are blocked until you{' '}
                  <Link to="/settings/subscription" className="underline font-medium">upgrade</Link>{' '}
                  or stay at or under the cap for seven consecutive days.</>
              : atCap
                ? <>You can keep importing assets during the grace period.{' '}
                    <Link to="/settings/subscription" className="underline font-medium">
                      View your deadline and upgrade options
                    </Link>.</>
              : <>You are using {pctAssets.toFixed(1)}% of your {assetLimit} asset limit.{' '}
                  <Link to="/settings/subscription" className="underline font-medium">Upgrade your plan</Link>{' '}
                  to avoid running out.</>}
          </AlertDescription>
        </Alert>
      )}

      {renderStepIndicator()}

      {currentStep === 'upload' && renderUploadStep()}
      {currentStep === 'mapping' && renderMappingStep()}
      {currentStep === 'preview' && renderPreviewStep()}
      {currentStep === 'import' && renderImportStep()}
    </div>
  );
}
